import React, { useState } from 'react';
import { Box, Typography, Avatar, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { typoBase, thinBold } from '../styles/typography';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import ArticleIcon from '@mui/icons-material/Article';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import axios from '../utils/axiosConfig';
import { getAllDocuments } from '../redux/actions/documentActions';

const DocumentsCard = ({ documents = [] }) => {
  const { t } = useTranslation(['pages', 'common']);
  const dispatch = useDispatch();
  const [hoveredDocumentId, setHoveredDocumentId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const renderDocumentIcon = (type) => {
    switch (type) {
      case 'pdf':
        return <PictureAsPdfIcon sx={{ color: '#E32636', fontSize: '18px', marginRight: '10px' }} />;
      case 'word':
        return <ArticleIcon sx={{ color: '#2B80B6', fontSize: '18px', marginRight: '10px' }} />;
      case 'excel':
        return <InsertDriveFileIcon sx={{ color: '#217346', fontSize: '18px', marginRight: '10px' }} />;
      case 'image':
        return <ImageIcon sx={{ color: '#555', fontSize: '18px', marginRight: '10px' }} />;
      default:
        return <InsertDriveFileIcon sx={{ color: '#555', fontSize: '18px', marginRight: '10px' }} />;
    }
  };

  // const formatDate = (dateString) => {
  //   const date = new Date(dateString);
  //   return date.toLocaleDateString('en-GB', { 
  //     day: '2-digit',
  //     month: '2-digit',
  //     year: 'numeric' 
  //   });
  // };
  const formatDate = (dateString) => {
    if (!dateString) return '';
  
    const date = new Date(dateString);
  
    const formattedDate = date.toLocaleDateString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).replaceAll('/', '-'); // Replace slashes with dashes
  
    return `${formattedDate}`;
  };
  
  const handleViewDocument = async (documentId, documentType) => {
    try {
      const { data } = await axios.get(`/api/documents/download/${documentId}`);
      if (data) {
        // If it's an image, open directly in a new tab
        if (documentType === "image") {
          window.open(data.fileUrl, "_blank");
        } else {
          // For other document types, use Google Docs viewer
          const viewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
            data.fileUrl
          )}&embedded=true`;
          window.open(viewerUrl, "_blank");
        }
      }
    } catch (error) {
      console.error("Error viewing document:", error);
    }
  };

  const handleDeleteClick = (documentId, e) => {
    e.stopPropagation();
    setDocumentToDelete(documentId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (documentToDelete) {
      try {
        await axios.delete(`/api/documents/${documentToDelete}`);
        await dispatch(getAllDocuments());
      } catch (error) {
        console.error('Error deleting document:', error);
      }
    }
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDocumentToDelete(null);
  };

  return (
    <Box>
      {documents.length > 0 ? (
        documents.slice(0, 20).map((document) => (
          <Box 
            key={document._id}
            display="flex"
            alignItems="center"
            onMouseEnter={() => setHoveredDocumentId(document._id)}
            onMouseLeave={() => setHoveredDocumentId(null)}
            sx={{ 
              borderBottom: '1px solid #ECECEC', 
              padding: '8px 0',
              '&:hover': {
                backgroundColor: '#ecf5fb',
              },
            }}
          >
            {renderDocumentIcon(document.type)}
            <Box flex={1}>
              <Typography 
                variant="body1" 
                onClick={() => handleViewDocument(document._id, document.type)}
                style={{ 
                  ...typoBase, 
                  color: '#005b92',
                  cursor: 'pointer',
                }}
                sx={{
                  '&:hover': {
                    textDecoration: 'underline',
                  }
                }}
              >
                {document.title}
              </Typography>
              <Typography variant="caption" style={{ color: '#777', fontSize: '11px' }}>
                {formatDate(document.modifiedDate)}
              </Typography>
            </Box>
            {document.owner && (
              <Avatar
                alt={document.owner.firstName && document.owner.lastName
                  ? `${document.owner.firstName} ${document.owner.lastName}`
                  : "N/A"}
                src={document.owner.avatar || "/static/images/avatar/1.jpg"}
                sx={{
                  width: "20px",
                  height: "20px",
                }}
              />
            )}
            {hoveredDocumentId === document._id && (
              <IconButton
                size="small"
                onClick={(e) => handleDeleteClick(document._id, e)}
                sx={{
                  color: '#666',
                  '&:hover': {
                    color: '#d32f2f',
                  },
                  marginLeft: '8px',
                }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            )}
          </Box>
        ))
      ) : (
        <Box display="flex" justifyContent="center" alignItems="center" height="100%" p={2}>
          <Typography variant="body1" style={{ color: '#777' }}>
            {t('common:no_data_available')}
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
      >
        <DialogTitle>{t('common:confirm_delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('common:delete_confirmation_message')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            {t('common:cancel')}
          </Button>
          <Button onClick={handleConfirmDelete} color="error" autoFocus>
            {t('common:delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DocumentsCard; 